<template>
  <v-dialog v-model="showDialog" max-width="600">
    <v-card>
      <v-card-title>{{ editingItem ? 'Edit Item' : 'Add New Items' }}</v-card-title>
      <v-card-text>
        <v-form ref="itemForm" v-model="itemValid" @submit.prevent="editingItem ? saveItem() : addItemToList()">
          <!-- Single Item Edit Mode -->
          <div v-if="editingItem">
            <v-text-field
              v-model="newItem.name"
              label="Name"
              :rules="[v => !!v || 'Name is required']"
              required
              @keydown.enter.prevent="saveItem"
            ></v-text-field>
            
            <!-- Collapsible Description for Edit Mode -->
            <div class="mb-3">
              <v-btn
                variant="text"
                size="small"
                @click="showDescription = !showDescription"
                class="px-0"
              >
                <v-icon>{{ showDescription ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                Description (optional)
              </v-btn>
              <v-textarea
                v-if="showDescription"
                v-model="newItem.description"
                label="Description"
                rows="2"
                class="mt-2"
                @keydown.enter.prevent="saveItem"
              ></v-textarea>
            </div>
            
            <v-text-field
              v-model.number="newItem.quantity"
              :label="newItem.quantity_type === 'fixed' ? 'Quantity' : 'Quantity per period'"
              type="number"
              min="1"
              :rules="quantityRules"
              required
              @keydown.enter.prevent="saveItem"
            ></v-text-field>
            
            <v-radio-group
              v-model="newItem.quantity_type"
              inline
              class="mt-2"
            >
              <v-radio label="Fixed quantity" value="fixed"></v-radio>
              <v-radio label="Per days" value="per_days"></v-radio>
            </v-radio-group>
            
            <v-text-field
              v-if="newItem.quantity_type === 'per_days'"
              v-model.number="newItem.per_days"
              label="Every X days"
              type="number"
              min="1"
              :rules="[v => !!v || 'Days is required', v => v >= 1 || 'Days must be at least 1']"
              required
              hint="e.g., 1 pair of pants every 3 days"
              @keydown.enter.prevent="saveItem"
            ></v-text-field>
            
            <v-combobox
              v-model="selectedGroup"
              :items="groupOptions"
              item-title="name"
              item-value="id"
              label="Group (optional)"
              hint="Select existing group or type new group name"
              clearable
              @update:search="newGroupName = $event"
            >
              <template #no-data>
                <v-list-item>
                  <v-list-item-title>
                    Press enter to create "<strong>{{ newGroupName }}</strong>"
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-combobox>
          </div>
          
          <!-- Multiple Items Add Mode -->
          <div v-else>
            <!-- Group Selection for All Items -->
            <v-combobox
              v-model="selectedGroup"
              :items="groupOptions"
              item-title="name"
              item-value="id"
              label="Group (optional) - applies to all items"
              hint="Select existing group or type new group name"
              clearable
              class="mb-4"
              @update:search="newGroupName = $event"
            >
              <template #no-data>
                <v-list-item>
                  <v-list-item-title>
                    Press enter to create "<strong>{{ newGroupName }}</strong>"
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-combobox>
            
            <!-- Items List -->
            <div v-if="itemsToAdd.length > 0" class="mb-4">
              <v-subheader class="px-0">Items to Add:</v-subheader>
              <v-list density="compact">
                <v-list-item
                  v-for="(item, index) in itemsToAdd"
                  :key="index"
                  class="px-0"
                >
                  <template #prepend>
                    <v-btn
                      icon="mdi-delete"
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="removeItemFromList(index)"
                    ></v-btn>
                  </template>
                  
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  
                  <template #append>
                    <div class="d-flex align-center">
                      <!-- Description Toggle -->
                      <v-btn
                        icon="mdi-text"
                        size="x-small"
                        variant="text"
                        :color="item.description ? 'primary' : 'grey'"
                        @click="toggleItemDescription(index)"
                        class="mr-2"
                      ></v-btn>
                      
                      <!-- Quantity Controls -->
                      <v-btn
                        icon="mdi-minus"
                        size="x-small"
                        variant="text"
                        @click="decreaseQuantity(index)"
                        :disabled="item.quantity <= 1"
                      ></v-btn>
                      <span class="mx-2 text-body-2" style="min-width: 20px; text-align: center;">{{ item.quantity }}</span>
                      <v-btn
                        icon="mdi-plus"
                        size="x-small"
                        variant="text"
                        @click="increaseQuantity(index)"
                      ></v-btn>
                    </div>
                  </template>
                </v-list-item>
                
                <!-- Description field for items -->
                <v-list-item
                  v-for="(item, index) in itemsToAdd.filter(item => item.showDescription)"
                  :key="'desc-' + index"
                  class="px-0 pt-0"
                >
                  <v-textarea
                    v-model="item.description"
                    label="Description"
                    rows="2"
                    density="compact"
                    variant="outlined"
                    class="mt-1"
                  ></v-textarea>
                </v-list-item>
              </v-list>
            </div>
            
            <!-- Add New Item Input -->
            <v-text-field
              v-model="currentItemName"
              label="Item name"
              :rules="[v => !!v || 'Name is required']"
              @keydown.enter.prevent="addItemToList"
              placeholder="Enter item name and press Enter or Add"
            >
              <template #append-inner>
                <v-btn
                  icon="mdi-plus"
                  size="small"
                  variant="text"
                  color="primary"
                  @click="addItemToList"
                  :disabled="!currentItemName?.trim()"
                ></v-btn>
              </template>
            </v-text-field>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancelItemEdit">Cancel</v-btn>
        <v-btn 
          ref="saveButton" 
          color="primary" 
          @click="saveItem" 
          :disabled="editingItem ? !itemValid : itemsToAdd.length === 0"
        >
          {{ editingItem ? 'Update' : `Add ${itemsToAdd.length} Item${itemsToAdd.length !== 1 ? 's' : ''}` }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import { useListStore } from '../stores/list'
import { useGroupStore } from '../stores/group'

export default {
  name: 'AddItemDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    editingItem: {
      type: Object,
      default: null
    },
    groupOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'saved', 'message'],
  setup(props, { emit }) {
    const listStore = useListStore()
    const groupStore = useGroupStore()

    const showDialog = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      }
    })

    const itemForm = ref(null)
    const saveButton = ref(null)
    const itemValid = ref(false)
    const showDescription = ref(false)
    const selectedGroup = ref(null)
    const newGroupName = ref('')

    const newItem = ref({
      name: '',
      description: '',
      quantity: 1,
      quantity_type: 'fixed',
      per_days: null,
      group_id: null
    })

    const itemsToAdd = ref([])
    const currentItemName = ref('')

    const quantityRules = [
      v => !!v || 'Quantity is required',
      v => v >= 1 || 'Quantity must be at least 1',
      v => Number.isInteger(v) || 'Quantity must be a whole number'
    ]

    const addItemToList = () => {
      const name = currentItemName.value?.trim()
      if (!name) return
      
      itemsToAdd.value.push({
        name: name,
        description: '',
        quantity: 1,
        quantity_type: 'fixed',
        per_days: null,
        showDescription: false
      })
      
      currentItemName.value = ''
    }

    const removeItemFromList = (index) => {
      itemsToAdd.value.splice(index, 1)
    }

    const increaseQuantity = (index) => {
      itemsToAdd.value[index].quantity++
    }

    const decreaseQuantity = (index) => {
      if (itemsToAdd.value[index].quantity > 1) {
        itemsToAdd.value[index].quantity--
      }
    }

    const toggleItemDescription = (index) => {
      itemsToAdd.value[index].showDescription = !itemsToAdd.value[index].showDescription
    }

    const cancelItemEdit = () => {
      newItem.value = { name: '', description: '', quantity: 1, quantity_type: 'fixed', per_days: null, group_id: null }
      selectedGroup.value = null
      newGroupName.value = ''
      showDescription.value = false
      itemsToAdd.value = []
      currentItemName.value = ''
      showDialog.value = false
    }

    const saveItem = async () => {
      if (props.editingItem) {
        // Single item edit mode
        if (!itemValid.value) return
        
        try {
          // Handle group creation/selection
          let groupId = null
          
          if (selectedGroup.value) {
            if (typeof selectedGroup.value === 'string') {
              // User typed a new group name
              const groupResult = await groupStore.createGroup({ 
                name: selectedGroup.value, 
                packlist_id: listStore.currentList.id 
              })
              if (groupResult.success) {
                groupId = groupResult.group.id
                emit('message', { message: `Group "${selectedGroup.value}" created!`, color: 'info' })
              } else {
                emit('message', { message: groupResult.error, color: 'error' })
                return
              }
            } else if (selectedGroup.value.id) {
              // User selected existing group
              groupId = selectedGroup.value.id
            }
          }
          
          const itemData = {
            ...newItem.value,
            group_id: groupId
          }
          
          const result = await listStore.updateItem(props.editingItem.id, itemData)
          
          if (result.success) {
            emit('message', { message: 'Item updated!', color: 'success' })
            emit('saved')
            cancelItemEdit()
          } else {
            emit('message', { message: result.error, color: 'error' })
          }
        } catch (error) {
          emit('message', { message: 'An error occurred while updating the item', color: 'error' })
        }
      } else {
        // Multiple items add mode
        if (itemsToAdd.value.length === 0) return
        
        try {
          // Handle group creation/selection
          let groupId = null
          
          if (selectedGroup.value) {
            if (typeof selectedGroup.value === 'string') {
              // User typed a new group name
              const groupResult = await groupStore.createGroup({ 
                name: selectedGroup.value, 
                packlist_id: listStore.currentList.id 
              })
              if (groupResult.success) {
                groupId = groupResult.group.id
                emit('message', { message: `Group "${selectedGroup.value}" created!`, color: 'info' })
              } else {
                emit('message', { message: groupResult.error, color: 'error' })
                return
              }
            } else if (selectedGroup.value.id) {
              // User selected existing group
              groupId = selectedGroup.value.id
            }
          }
          
          // Add all items
          let successCount = 0
          const totalItems = itemsToAdd.value.length
          
          for (const item of itemsToAdd.value) {
            const itemData = {
              name: item.name,
              description: item.description || '',
              quantity: item.quantity,
              quantity_type: item.quantity_type || 'fixed',
              per_days: item.per_days || null,
              group_id: groupId
            }
            
            const result = await listStore.addItem(listStore.currentList.id, itemData)
            if (result.success) {
              successCount++
            }
          }
          
          if (successCount === totalItems) {
            emit('message', { message: `${successCount} item${successCount !== 1 ? 's' : ''} added successfully!`, color: 'success' })
            emit('saved')
            cancelItemEdit()
          } else {
            emit('message', { message: `${successCount}/${totalItems} items added. Some failed to save.`, color: 'warning' })
          }
        } catch (error) {
          emit('message', { message: 'An error occurred while saving items', color: 'error' })
        }
      }
    }

    // Watch for editing item changes to populate form
    watch(() => props.editingItem, (editingItem) => {
      if (editingItem) {
        newItem.value = {
          name: editingItem.name,
          description: editingItem.description || '',
          quantity: editingItem.quantity || 1,
          quantity_type: editingItem.quantity_type || 'fixed',
          per_days: editingItem.per_days || null,
          group_id: editingItem.group_id || null
        }
        selectedGroup.value = editingItem.group_id ? groupStore.getGroupById(editingItem.group_id) : null
      } else {
        // Clear form for add mode
        newItem.value = { name: '', description: '', quantity: 1, quantity_type: 'fixed', per_days: null, group_id: null }
        selectedGroup.value = null
        newGroupName.value = ''
        showDescription.value = false
        itemsToAdd.value = []
        currentItemName.value = ''
      }
    }, { immediate: true })

    // Focus save button when dialog opens
    watch(showDialog, (newVal) => {
      if (newVal) {
        nextTick(() => {
          if (saveButton.value?.$el) {
            saveButton.value.$el.focus()
          }
        })
      }
    })

    return {
      showDialog,
      itemForm,
      saveButton,
      itemValid,
      showDescription,
      selectedGroup,
      newGroupName,
      newItem,
      itemsToAdd,
      currentItemName,
      quantityRules,
      addItemToList,
      removeItemFromList,
      increaseQuantity,
      decreaseQuantity,
      toggleItemDescription,
      cancelItemEdit,
      saveItem
    }
  }
}
</script>